import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { formatStatFraction } from './packagingSupplierStats'

export type PackedItemsFunnelProgressStats = {
  total: number
  unaddressed: number
  inProgress: number
  completed: number
}

type FunnelLegendLabels = {
  unaddressed: { label: string; tooltip: string }
  inProgress: { label: string; tooltip: string }
  completed: { label: string; tooltip: string }
}

type PackedItemsFunnelProgressBarProps = {
  stats: PackedItemsFunnelProgressStats
  labels: FunnelLegendLabels
}

type FunnelSegment = {
  key: keyof PackedItemsFunnelProgressStats
  count: number
  className: string
  label: string
  tooltip: string
}

function segmentWidth(count: number, total: number): string {
  if (total <= 0 || count <= 0) {
    return '0%'
  }
  return `${(count / total) * 100}%`
}

export function PackedItemsFunnelProgressBar({
  stats,
  labels,
}: PackedItemsFunnelProgressBarProps) {
  const segments: FunnelSegment[] = [
    {
      key: 'unaddressed',
      count: stats.unaddressed,
      className: 'bg-destructive',
      label: labels.unaddressed.label,
      tooltip: labels.unaddressed.tooltip,
    },
    {
      key: 'inProgress',
      count: stats.inProgress,
      className: 'bg-amber-500',
      label: labels.inProgress.label,
      tooltip: labels.inProgress.tooltip,
    },
    {
      key: 'completed',
      count: stats.completed,
      className: 'bg-green-600 dark:bg-green-500',
      label: labels.completed.label,
      tooltip: labels.completed.tooltip,
    },
  ]

  return (
    <div className='space-y-3 rounded-lg border p-4'>
      <div
        className='flex h-3 w-full overflow-hidden rounded-full bg-muted'
        role='img'
        aria-label={[
          labels.unaddressed.label,
          labels.inProgress.label,
          labels.completed.label,
        ].join(', ')}
      >
        {stats.total > 0 ? (
          segments.map((segment) => {
            if (segment.count <= 0) {
              return null
            }
            return (
              <div
                key={segment.key}
                className={segment.className}
                style={{ width: segmentWidth(segment.count, stats.total) }}
                title={`${segment.label}: ${formatStatFraction(segment.count, stats.total)}`}
              />
            )
          })
        ) : null}
      </div>
      <div className='flex flex-wrap gap-x-6 gap-y-2 text-sm'>
        {segments.map((segment) => (
          <Tooltip key={segment.key}>
            <TooltipTrigger asChild>
              <span className='inline-flex cursor-default items-center gap-2'>
                <span
                  className={`size-2.5 shrink-0 rounded-full ${segment.className}`}
                  aria-hidden
                />
                <span className='text-muted-foreground'>{segment.label}</span>
                <span className='font-medium tabular-nums'>
                  {formatStatFraction(segment.count, stats.total)}
                </span>
              </span>
            </TooltipTrigger>
            <TooltipContent side='bottom' className='max-w-xs'>
              {segment.tooltip}
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </div>
  )
}
